import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLocation } from "react-router-dom";
import RecordPayment from "../components/collection/RecordPayment";
import CollectionConfiguration from "../components/collection/CollectionConfiguration";
import AllPayments from "../components/collection/AllPayments";
import CollectionManagement from "../components/collection/CollectionManagement";

// Constants
export const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
export const QUARTER_MONTHS = [
  { value: "Q1", label: "January (Q1)" },
  { value: "Q2", label: "April (Q2)" },
  { value: "Q3", label: "July (Q3)" },
  { value: "Q4", label: "October (Q4)" },
];

export interface Collection {
  id: number;
  receiptNumber: string;
  consumerName: string;
  accountNumber: string;
  amount: string;
  paymentMethod: string;
  paymentDate: string;
  collectedBy: string;
  status: string;
  serviceType: string;
  cashAccount: string | null;
  bankAccount: string | null;
  cashAmount: number;
  bankAmount: number;
}

export const INITIAL_COLLECTIONS: Collection[] = [
  {
    id: 1,
    receiptNumber: "RCT-10045",
    consumerName: "Mary Johnson",
    accountNumber: "RES-1001",
    amount: "$78.50",
    paymentMethod: "Credit Card",
    paymentDate: "2025-05-15",
    collectedBy: "John Doe",
    status: "Completed",
    serviceType: "Water Supply - Residential",
    cashAccount: null,
    bankAccount: null,
    cashAmount: 0,
    bankAmount: 0,
  },
  {
    id: 2,
    receiptNumber: "RCT-10046",
    consumerName: "Central Hospital",
    accountNumber: "COM-2002",
    amount: "$423.75",
    paymentMethod: "Bank Transfer",
    paymentDate: "2025-05-16",
    collectedBy: "Jane Smith",
    status: "Completed",
    serviceType: "Water Supply - Commercial",
    cashAccount: null,
    bankAccount: null,
    cashAmount: 0,
    bankAmount: 0,
  },
  {
    id: 3,
    receiptNumber: "RCT-10047",
    consumerName: "Green Park Community",
    accountNumber: "COM-3003",
    amount: "$215.25",
    paymentMethod: "Check",
    paymentDate: "2025-05-16",
    collectedBy: "John Doe",
    status: "Processing",
    serviceType: "Sewage Treatment",
    cashAccount: null,
    bankAccount: null,
    cashAmount: 0,
    bankAmount: 0,
  },
];

export const MOCK_CONSUMERS = [
  {
    id: 1,
    name: "Mary Johnson",
    accountNumber: "RES-1001",
    category: "Residential",
    division: "North",
    zone: "A",
    contactNo: "1234567890",
  },
  {
    id: 2,
    name: "Central Hospital",
    accountNumber: "COM-2002",
    category: "Commercial",
    division: "Central",
    zone: "Business District",
    contactNo: "1234567890",
  },
  {
    id: 3,
    name: "Green Park Community",
    accountNumber: "COM-3003",
    category: "Commercial",
    division: "West",
    zone: "C",
    contactNo: "1234567890",
  },
  {
    id: 4,
    name: "City School",
    accountNumber: "GOV-4004",
    category: "Institution",
    division: "East",
    zone: "Educational Zone",
    contactNo: "1234567890",
  },
  {
    id: 5,
    name: "Main Street Shops",
    accountNumber: "COM-5005",
    category: "Commercial",
    division: "North",
    zone: "B",
    contactNo: "1234567890",
  },
];

export const MOCK_SERVICES = [
  "Water Supply - Residential",
  "Water Supply - Commercial",
  "Sewage Treatment",
  "Water Meter Installation",
  "Plumbing Maintenance",
  "Waste Management",
];

export const MOCK_CATEGORIES = [
  "Residential",
  "Commercial",
  "Institution",
  "Government",
  "Industrial",
];
export const MOCK_DIVISIONS = ["North", "South", "East", "West", "Central"];
export const MOCK_ZONES = [
  "A",
  "B",
  "C",
  "Business District",
  "Industrial Area",
  "Educational Zone",
  "Government Complex",
];
export const MOCK_COLLECTORS = [
  "John Doe",
  "Jane Smith",
  "Michael Brown",
  "Sarah Davis",
  "David Miller",
];

export const MOCK_CONSUMER_DUES = [
  {
    id: 1,
    consumerName: "Mary Johnson",
    accountNumber: "RES-1001",
    totalDue: 125.75,
    category: "Residential",
    division: "North",
    zone: "A",
    collections: [
      {
        id: 101,
        name: "Water Supply - April",
        service: "Water Supply - Residential",
        dueAmount: 75.5,
        dueDate: "2025-04-30",
      },
      {
        id: 102,
        name: "Sewage - April",
        service: "Sewage Treatment",
        dueAmount: 50.25,
        dueDate: "2025-04-25",
      },
    ],
  },
  {
    id: 2,
    consumerName: "Central Hospital",
    accountNumber: "COM-2002",
    totalDue: 780.0,
    category: "Commercial",
    division: "Central",
    zone: "Business District",
    collections: [
      {
        id: 201,
        name: "Water Supply - Q2",
        service: "Water Supply - Commercial",
        dueAmount: 450.0,
        dueDate: "2025-05-15",
      },
      {
        id: 202,
        name: "Waste Management - May",
        service: "Waste Management",
        dueAmount: 330.0,
        dueDate: "2025-05-20",
      },
    ],
  },
  {
    id: 3,
    consumerName: "Green Park Community",
    accountNumber: "COM-3003",
    totalDue: 215.25,
    category: "Commercial",
    division: "West",
    zone: "C",
    collections: [
      {
        id: 301,
        name: "Water Supply - May",
        service: "Water Supply - Commercial",
        dueAmount: 215.25,
        dueDate: "2025-05-30",
      },
    ],
  },
  {
    id: 4,
    consumerName: "City School",
    accountNumber: "GOV-4004",
    totalDue: 420.5,
    category: "Institution",
    division: "East",
    zone: "Educational Zone",
    collections: [
      {
        id: 401,
        name: "Water Supply - May",
        service: "Water Supply - Commercial",
        dueAmount: 225.0,
        dueDate: "2025-05-25",
      },
      {
        id: 402,
        name: "Plumbing Maintenance",
        service: "Plumbing Maintenance",
        dueAmount: 195.5,
        dueDate: "2025-05-15",
      },
    ],
  },
];

export const MOCK_COLLECTOR_ACCOUNTS = [
  { id: 1, name: "John Doe - Cash Account", type: "Cash", balance: 1250.0 },
  { id: 2, name: "John Doe - Bank Account", type: "Bank", balance: 3780.5 },
  { id: 3, name: "Jane Smith - Cash Account", type: "Cash", balance: 2340.75 },
  { id: 4, name: "Jane Smith - Bank Account", type: "Bank", balance: 4125.0 },
  {
    id: 5,
    name: "Michael Brown - Cash Account",
    type: "Cash",
    balance: 1890.25,
  },
  {
    id: 6,
    name: "Michael Brown - Bank Account",
    type: "Bank",
    balance: 2890.5,
  },
];

export const MOCK_SERVICE_ACCOUNTS = [
  {
    id: 101,
    name: "Water Supply Revenue",
    service: "Water Supply - Residential",
    balance: 12500.0,
  },
  {
    id: 102,
    name: "Commercial Water Revenue",
    service: "Water Supply - Commercial",
    balance: 28750.5,
  },
  {
    id: 103,
    name: "Sewage Treatment Revenue",
    service: "Sewage Treatment",
    balance: 7890.25,
  },
  {
    id: 104,
    name: "Waste Management Revenue",
    service: "Waste Management",
    balance: 6540.75,
  },
  {
    id: 105,
    name: "Plumbing Services Revenue",
    service: "Plumbing Maintenance",
    balance: 4320.0,
  },
];

interface CollectionPageProps {
  activeTab?: string;
}

const CollectionPage = ({ activeTab = "payments" }: CollectionPageProps) => {
  const location = useLocation();
  const [activeTabState, setActiveTabState] = useState(activeTab);
  const [collections, setCollections] =
    useState<Collection[]>(INITIAL_COLLECTIONS);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isEditPaymentMode, setIsEditPaymentMode] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Collection | null>(null);

  useEffect(() => {
    setActiveTabState(activeTab);
  }, [activeTab]);

  useEffect(() => {
    const editPayment = location.state?.editPayment;
    if (editPayment) {
      setIsEditPaymentMode(true);
      setEditingPayment(editPayment);
      setShowPaymentForm(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        {/* <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Collection Management
          </h1>
          <p className="text-muted-foreground">
            Record payments and manage collection configurations
          </p>
        </div> */}
      </div>
      <Tabs value={activeTabState} onValueChange={setActiveTabState}>
        <TabsContent value="payments" className="space-y-4">
          <AllPayments collections={collections} />
          <RecordPayment
            collections={collections}
            setCollections={setCollections}
            showPaymentForm={showPaymentForm}
            setShowPaymentForm={setShowPaymentForm}
            isEditPaymentMode={isEditPaymentMode}
            setIsEditPaymentMode={setIsEditPaymentMode}
            editingPayment={editingPayment}
            setEditingPayment={setEditingPayment}
          />
        </TabsContent>
        {/* <TabsContent value="configs" className="space-y-4">
          <CollectionConfiguration />
        </TabsContent> */}
        <TabsContent value="configs" className="space-y-4">
          <CollectionManagement />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CollectionPage;
